import { useRef, memo, useMemo, useCallback, ReactNode, useEffect, useState } from 'react';
import { useVirtualizer } from '@tanstack/react-virtual';
import { cn } from '@/lib/utils';
import { SavesGridCard } from '@/components/saves/SavesGridCard';
import { ProductListItem } from '@/components/saves/ProductListItem';
import type { Product } from '@/types/product';
import { useDismissedTutorials } from '@/hooks/useDismissedTutorials';
import type { ViewMode } from '@/components/saves/SavesTabNav';

interface VirtualizedProductGridProps {
  products: (Product & { 
    url?: string; 
    isSavedItem?: boolean; 
    sourceType?: string; 
    price: number | null;
    focalPoint?: { x: number; y: number } | null;
    recommendationImages?: string[];
    note?: string;
    ai_summary?: string;
    pinned_at?: string | null;
    isExtracting?: boolean;
    ai_category?: string | null;
  })[];
  onDelete?: (id: string, name: string) => void;
  className?: string;
  gap?: number;
  estimateRowHeight?: number;
  // Selection mode props
  selectionMode?: boolean;
  selectedIds?: Set<string>;
  onToggleSelect?: (id: string) => void;
  onLongPressSelect?: (id: string) => void;
  // Collection indicator
  itemsInCollections?: Set<string>;
  // Reported items indicator
  reportedItemIds?: Set<string>;
  // View mode
  viewMode?: ViewMode;
  // Optional element to render after items in grid (e.g., nudge card)
  renderAfterItems?: ReactNode;
}

/**
 * Virtualized grid/list for displaying large lists of products.
 * Only renders visible rows + a small overscan buffer for smooth scrolling.
 */
export const VirtualizedProductGrid = memo(function VirtualizedProductGrid({
  products,
  onDelete,
  className,
  gap = 16,
  estimateRowHeight = 260,
  selectionMode = false,
  selectedIds,
  onToggleSelect,
  onLongPressSelect,
  itemsInCollections,
  reportedItemIds,
  viewMode = 'grid',
  renderAfterItems,
}: VirtualizedProductGridProps) {
  const parentRef = useRef<HTMLDivElement>(null);
  const { isDismissed, dismiss } = useDismissedTutorials();
  
  // Detect if we're inside the phone frame (constrained width on desktop)
  const [isInPhoneFrame, setIsInPhoneFrame] = useState(false);
  useEffect(() => {
    const el = parentRef.current ?? document.querySelector('.phone-frame-root');
    setIsInPhoneFrame(!!document.querySelector('.phone-frame-root'));
  }, []);
  
  // Show tap hint only on the very first saved item
  const showFirstItemHint = products.length === 1 && !isDismissed('first_item_tap_hint');
  const showTryOnNudge = products.length === 1 && !isDismissed('first_item_tryon_nudge');
  
  const handleTapHintDismiss = useCallback(() => {
    dismiss('first_item_tap_hint');
  }, [dismiss]);

  const handleTryOnNudgeDismiss = useCallback(() => {
    dismiss('first_item_tryon_nudge');
  }, [dismiss]);

  // In phone frame, always use 2 columns to mirror mobile
  const maxItemsPerRow = viewMode === 'grid' ? (isInPhoneFrame ? 2 : 4) : 1;
  const rows = useMemo(() => {
    const result: typeof products[] = [];
    for (let i = 0; i < products.length; i += maxItemsPerRow) {
      result.push(products.slice(i, i + maxItemsPerRow));
    }
    return result;
  }, [products, maxItemsPerRow]);

  // List view now uses same square aspect as grid - estimate based on viewport width + padding
  const listRowHeight = 260; // Square aspect ratio + text area below
  const rowHeight = viewMode === 'list' ? listRowHeight : estimateRowHeight;

  // Find the nearest scrollable ancestor to use as scroll element
  const [scrollElement, setScrollElement] = useState<HTMLElement | null>(null);
  useEffect(() => {
    const el = parentRef.current;
    if (!el) return;
    let parent = el.parentElement;
    while (parent) {
      const style = getComputedStyle(parent);
      if (style.overflowY === 'auto' || style.overflowY === 'scroll') {
        setScrollElement(parent);
        return;
      }
      parent = parent.parentElement;
    }
    // Fallback to document scrolling element
    setScrollElement(document.documentElement);
  }, []);

  const virtualizer = useVirtualizer({
    count: rows.length,
    getScrollElement: () => scrollElement,
    estimateSize: () => rowHeight,
    overscan: 3,
  });

  const virtualItems = virtualizer.getVirtualItems();

  // If less than 20 items, don't virtualize (overhead not worth it)
  if (products.length < 20) {
    if (viewMode === 'list') {
      return (
        <div className={cn('flex flex-col gap-3', className)}>
          {products.map((product, index) => (
            <div
              key={product.id}
              style={{ animationDelay: `${index * 50}ms`, contentVisibility: 'auto', containIntrinsicSize: 'auto 260px' }}
              className="animate-fade-up"
            >
              <ProductListItem
                product={product}
                onDelete={selectionMode ? undefined : (onDelete ? (id) => onDelete(id, product.name) : undefined)}
                selectionMode={selectionMode}
                isSelected={selectedIds?.has(product.id)}
                onToggleSelect={onToggleSelect}
                onLongPressSelect={onLongPressSelect}
                isInCollection={itemsInCollections?.has(product.id)}
                isReported={reportedItemIds?.has(product.id)}
                priority={index < 2}
              />
            </div>
          ))}
        </div>
      );
    }
    
    return (
      <div className={cn('grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3', className)}>
        {products.map((product, index) => (
          <div
            key={product.id}
            style={{ animationDelay: `${index * 50}ms`, contentVisibility: 'auto', containIntrinsicSize: 'auto 260px' }}
            className="animate-fade-up"
          >
            <SavesGridCard 
              product={product} 
              onDelete={selectionMode ? undefined : (onDelete ? (id) => onDelete(id, product.name) : undefined)}
              selectionMode={selectionMode}
              isSelected={selectedIds?.has(product.id)}
              onToggleSelect={onToggleSelect}
              onLongPressSelect={onLongPressSelect}
               showTapHint={showFirstItemHint && index === 0}
               onTapHintDismiss={handleTapHintDismiss}
               isInCollection={itemsInCollections?.has(product.id)}
               isReported={reportedItemIds?.has(product.id)}
               priority={index < 4}
               showTryOnNudge={showTryOnNudge && index === 0}
               onTryOnNudgeDismiss={handleTryOnNudgeDismiss}
            />
          </div>
        ))}
        {renderAfterItems}
      </div>
    );
  }

  return (
    <div
      ref={parentRef}
      className={cn('w-full', className)}
      style={{
        position: 'relative',
      }}
    >
      <div
        style={{
          height: `${virtualizer.getTotalSize()}px`,
          width: '100%',
          position: 'relative',
        }}
      >
        {virtualItems.map((virtualRow) => {
          const rowProducts = rows[virtualRow.index];
          return (
            <div
              key={virtualRow.key}
              data-index={virtualRow.index}
              ref={virtualizer.measureElement}
              style={{
                position: 'absolute',
                top: 0,
                left: 0,
                width: '100%',
                transform: `translateY(${virtualRow.start}px)`,
                paddingBottom: `${viewMode === 'list' ? 12 : gap}px`,
              }}
            >
            {viewMode === 'list' ? (
                <div className="flex flex-col gap-3">
                  {rowProducts.map((product) => (
                    <ProductListItem
                      key={product.id}
                      product={product}
                      onDelete={selectionMode ? undefined : (onDelete ? (id) => onDelete(id, product.name) : undefined)}
                      selectionMode={selectionMode}
                      isSelected={selectedIds?.has(product.id)}
                      onToggleSelect={onToggleSelect}
                      onLongPressSelect={onLongPressSelect}
                      isInCollection={itemsInCollections?.has(product.id)}
                      isReported={reportedItemIds?.has(product.id)}
                      priority={virtualRow.index < 2}
                    />
                  ))}
                </div>
              ) : (
              <div 
                className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4" 
                style={{ gap: '12px' }}
              >
                {rowProducts.map((product) => (
                  <SavesGridCard
                    key={product.id}
                    product={product}
                    onDelete={selectionMode ? undefined : (onDelete ? (id) => onDelete(id, product.name) : undefined)}
                    selectionMode={selectionMode}
                    isSelected={selectedIds?.has(product.id)}
                    onToggleSelect={onToggleSelect}
                    onLongPressSelect={onLongPressSelect}
                    isInCollection={itemsInCollections?.has(product.id)}
                    isReported={reportedItemIds?.has(product.id)}
                    priority={virtualRow.index < 2}
                  />
                ))}
              </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
});

export default VirtualizedProductGrid;
